/* eslint-disable */
import "./DemoRequests.css";
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  appendDemoRequestNote,
  buildDemoEmail,
  CONTRACT_STATUSES,
  deleteDemoRequest,
  DEMO_REQUEST_STATUSES,
  getMillis,
  normaliseDemoStatus,
  ONBOARDING_STATUSES,
  PRESENTPLUS_MODULES,
  statusLabel,
  subscribeToDemoRequests,
  updateDemoRequestSchoolProfile,
  updateDemoRequestStatus,
} from "../services/demoRequestsService";

const LOGO_URL = "https://presentplus.uk/presentplus-logo-white.png";

const EMAIL_TEMPLATES = [
  { value: "initial", label: "Initial reply" },
  { value: "follow_up", label: "Follow-up" },
  { value: "booked", label: "Meeting booked" },
  { value: "after_demo", label: "After demo" },
  { value: "proposal", label: "Proposal / next steps" },
];

function firstValue(item, keys, fallback = "—") {
  for (const key of keys) {
    if (
      item?.[key] !== undefined &&
      item?.[key] !== null &&
      String(item[key]).trim()
    ) {
      return item[key];
    }
  }
  return fallback;
}

function getSchool(item) {
  return firstValue(
    item,
    ["schoolName", "school", "organisation", "organization"],
    "Unknown school"
  );
}

function getName(item) {
  return firstValue(item, [
    "name",
    "fullName",
    "contactName",
    "contact",
    "representativeName",
  ]);
}

function getEmail(item) {
  return firstValue(item, ["email", "contactEmail", "workEmail"]);
}

function getPhone(item) {
  return firstValue(item, [
    "phone",
    "phoneNumber",
    "telephone",
    "contactNumber",
  ]);
}

function getRole(item) {
  return firstValue(item, ["role", "jobTitle", "position"]);
}

function getFocus(item) {
  return firstValue(
    item,
    ["demoFocus", "interest", "message", "requirements", "focus"],
    "Full PresentPlus platform"
  );
}

function requestDate(item) {
  return item?.createdAt || item?.submittedAt || item?.receivedAt || item?.timestamp;
}

function toDate(value) {
  const millis = getMillis(value);
  return millis ? new Date(millis) : null;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return "—";

  return date.toLocaleString("en-GB", {
    timeZone: "Europe/London",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusTone(status) {
  switch (normaliseDemoStatus(status)) {
    case "contacted":
      return { background: "#dbeafe", color: "#1d4ed8" };
    case "meeting_booked":
      return { background: "#fef3c7", color: "#92400e" };
    case "demo_completed":
      return { background: "#ede9fe", color: "#6d28d9" };
    case "closed":
      return { background: "#e2e8f0", color: "#334155" };
    default:
      return { background: "#dcfce7", color: "#166534" };
  }
}

function defaultProfile(item) {
  const profile = item?.schoolProfile || {};
  return {
    modulesOfInterest: Array.isArray(profile.modulesOfInterest)
      ? profile.modulesOfInterest
      : [],
    meeting: {
      date: profile.meeting?.date || "",
      time: profile.meeting?.time || "",
      meetingLink: profile.meeting?.meetingLink || "",
      location: profile.meeting?.location || "",
    },
    contract: {
      status: profile.contract?.status || "not_started",
      plan: profile.contract?.plan || "",
      annualValue: profile.contract?.annualValue || "",
      renewalDate: profile.contract?.renewalDate || "",
    },
    onboarding: {
      status: profile.onboarding?.status || "not_started",
      progress: Number(profile.onboarding?.progress || 0),
    },
    tasks: Array.isArray(profile.tasks) ? profile.tasks : [],
  };
}

export default function DemoRequests() {
  const { profile } = useAuth() || {};
  const actorName =
    profile?.fullName ||
    profile?.displayName ||
    profile?.name ||
    "PresentPlus Super Admin";

  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profileForm, setProfileForm] = useState(defaultProfile(null));
  const [tasksText, setTasksText] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("initial");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    return subscribeToDemoRequests(
      (rows) => {
        setRequests(rows);
        setLoading(false);
        setSelected((current) =>
          current ? rows.find((row) => row.id === current.id) || null : current
        );
      },
      (err) => {
        console.error(err);
        setError(err?.message || "Demo requests could not be loaded.");
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    const next = defaultProfile(selected);
    setProfileForm(next);
    setTasksText(next.tasks.map((task) => task.text || task).join("\n"));
    setNoteDraft("");
    setEmailTemplate("initial");
    setDeleteText("");
  }, [selected?.id, selected?.schoolProfile?.updatedAt]);

  const stats = useMemo(() => {
    const result = {
      all: requests.length,
      new: 0,
      contacted: 0,
      meeting_booked: 0,
      demo_completed: 0,
      closed: 0,
    };

    requests.forEach((item) => {
      const status = normaliseDemoStatus(item.status);
      if (result[status] !== undefined) result[status] += 1;
      else result.new += 1;
    });

    return result;
  }, [requests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return requests.filter((item) => {
      const status = normaliseDemoStatus(item.status);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const schoolProfile = item.schoolProfile || {};

      const blob = [
        getSchool(item),
        getName(item),
        getEmail(item),
        getPhone(item),
        getRole(item),
        getFocus(item),
        item.notes,
        ...(schoolProfile.modulesOfInterest || []),
        schoolProfile.contract?.status,
        schoolProfile.contract?.plan,
        schoolProfile.onboarding?.status,
        ...(schoolProfile.tasks || []).map((task) => task.text || task),
      ]
        .join(" ")
        .toLowerCase();

      return matchesStatus && (!q || blob.includes(q));
    });
  }, [requests, search, statusFilter]);

  function showNotice(message) {
    setNotice(message);
    window.clearTimeout(showNotice.timer);
    showNotice.timer = window.setTimeout(() => setNotice(""), 3000);
  }

  async function changeStatus(nextStatus) {
    if (!selected?.id) return;

    try {
      setUpdatingStatus(true);
      setError("");
      await updateDemoRequestStatus(
        selected.id,
        selected.status,
        nextStatus,
        actorName
      );
      showNotice(`Status changed to ${statusLabel(nextStatus)}.`);
    } catch (err) {
      setError(err?.message || "The status could not be updated.");
    } finally {
      setUpdatingStatus(false);
    }
  }

  async function saveNote() {
    if (!selected?.id) return;

    try {
      setSavingNotes(true);
      setError("");
      await appendDemoRequestNote(
        selected.id,
        selected.notes,
        noteDraft,
        actorName
      );
      setNoteDraft("");
      showNotice("Internal note added.");
    } catch (err) {
      setError(err?.message || "The note could not be saved.");
    } finally {
      setSavingNotes(false);
    }
  }

  async function saveSchoolProfile() {
    if (!selected?.id) return;

    const previousTasks = Array.isArray(profileForm.tasks)
      ? profileForm.tasks
      : [];
    const taskLines = tasksText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const tasks = taskLines.map((line) => {
      const existing = previousTasks.find(
        (task) => String(task.text || task).trim() === line
      );
      return {
        text: line,
        completed: Boolean(existing?.completed),
      };
    });

    try {
      setSavingProfile(true);
      setError("");
      await updateDemoRequestSchoolProfile(
        selected.id,
        { ...profileForm, tasks },
        actorName
      );
      showNotice("School profile saved.");
    } catch (err) {
      setError(err?.message || "The school profile could not be saved.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleDelete() {
    if (!selected?.id || deleteText !== "DELETE") return;

    try {
      setDeleting(true);
      setError("");
      await deleteDemoRequest(selected.id);
      setSelected(null);
      setDeleteText("");
      showNotice("Demo request permanently deleted.");
    } catch (err) {
      setError(err?.message || "The demo request could not be deleted.");
    } finally {
      setDeleting(false);
    }
  }

  function toggleModule(module) {
    const current = profileForm.modulesOfInterest || [];
    const next = current.includes(module)
      ? current.filter((item) => item !== module)
      : [...current, module];

    setProfileForm((old) => ({ ...old, modulesOfInterest: next }));
  }

  function toggleTask(index) {
    const lines = tasksText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const current = lines.map((line) => {
      const existing = (profileForm.tasks || []).find(
        (task) => String(task.text || task).trim() === line
      );
      return { text: line, completed: Boolean(existing?.completed) };
    });

    current[index] = {
      ...current[index],
      completed: !current[index]?.completed,
    };

    setProfileForm((old) => ({ ...old, tasks: current }));
  }

  function openEmail() {
    const recipient = selected ? getEmail(selected) : "";
    if (!recipient || recipient === "—") {
      setError("This enquiry does not contain an email address.");
      return;
    }

    const email = buildDemoEmail({
      template: emailTemplate,
      contactName: getName(selected),
      schoolName: getSchool(selected),
    });

    window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(
      email.subject
    )}&body=${encodeURIComponent(email.body)}`;
  }

  const timeline = Array.isArray(selected?.timeline)
    ? [...selected.timeline].sort(
        (a, b) =>
          getMillis(b.date || b.createdAt) -
          getMillis(a.date || a.createdAt)
      )
    : [];

  const taskRows = tasksText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div style={styles.page}>
      <style>{`
        * { box-sizing: border-box; }
        .pp-input:focus,.pp-select:focus,.pp-textarea:focus {
          outline:none;border-color:#2563eb!important;
          box-shadow:0 0 0 4px rgba(37,99,235,.12)!important;
        }
        .pp-scroll::-webkit-scrollbar { width:8px;height:8px; }
        .pp-scroll::-webkit-scrollbar-thumb { background:rgba(37,99,235,.22);border-radius:999px; }
        .pp-row:hover { transform:translateY(-1px);box-shadow:0 12px 28px rgba(15,23,42,.07); }
        @media(max-width:900px){
          .pp-hero{grid-template-columns:1fr!important}
          .pp-stats{grid-template-columns:repeat(2,minmax(0,1fr))!important}
          .pp-row{grid-template-columns:48px 1fr!important}
          .pp-row-right{grid-column:2;justify-items:start!important}
          .pp-two{grid-template-columns:1fr!important}
        }
        @media(max-width:560px){
          .pp-page{padding:10px!important}
          .pp-stats{grid-template-columns:1fr!important}
          .pp-drawer{padding:15px!important}
        }
      `}</style>

      <section className="pp-hero" style={styles.hero}>
        <div>
          <div style={styles.brand}>
            <img src={LOGO_URL} alt="PresentPlus" style={styles.logo} />
            PresentPlus Sales CRM
          </div>
          <h1 style={styles.title}>School sales profiles</h1>
          <p style={styles.subtitle}>
            Manage every school from enquiry and demonstration through contract,
            onboarding and live customer status.
          </p>
          <button
            style={styles.backButton}
            onClick={() => (window.location.href = "/super-admin")}
          >
            ← Super Admin
          </button>
        </div>
        <div style={styles.heroCard}>
          <span style={styles.heroLabel}>Sales overview</span>
          <strong style={styles.heroNumber}>{stats.all - stats.closed}</strong>
          <span>{stats.new} new enquiries awaiting review</span>
        </div>
      </section>

      {error ? <div style={styles.error}>{error}</div> : null}
      {notice ? <div style={styles.success}>{notice}</div> : null}

      <section className="pp-stats" style={styles.stats}>
        {DEMO_REQUEST_STATUSES.map((item) => (
          <button
            key={item.value}
            style={{
              ...styles.stat,
              ...(statusFilter === item.value ? styles.statActive : {}),
            }}
            onClick={() =>
              setStatusFilter(statusFilter === item.value ? "all" : item.value)
            }
          >
            <span style={{ ...styles.pill, ...statusTone(item.value) }}>
              {item.label}
            </span>
            <strong style={styles.statNumber}>{stats[item.value]}</strong>
            <small style={styles.statNote}>
              {item.value === "new"
                ? "Awaiting first review"
                : item.value === "contacted"
                ? "Initial contact completed"
                : item.value === "meeting_booked"
                ? "Meetings scheduled"
                : item.value === "demo_completed"
                ? "Demonstrations delivered"
                : "Completed opportunities"}
            </small>
          </button>
        ))}
      </section>

      <section style={styles.listCard}>
        <div style={styles.toolbar}>
          <div>
            <div style={styles.eyebrow}>PresentPlus pipeline</div>
            <h2 style={styles.sectionTitle}>Schools and enquiries</h2>
          </div>
          <div style={styles.filters}>
            <input
              className="pp-input"
              style={styles.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search school, contact, modules, tasks..."
            />
            <select
              className="pp-select"
              style={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {DEMO_REQUEST_STATUSES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={styles.empty}>Loading demo requests...</div>
        ) : filtered.length ? (
          <div className="pp-scroll" style={styles.list}>
            {filtered.map((item) => (
              <button
                key={item.id}
                className="pp-row"
                style={styles.row}
                onClick={() => setSelected(item)}
              >
                <div style={styles.avatar}>
                  {String(getSchool(item)).slice(0, 1).toUpperCase()}
                </div>
                <div style={styles.rowMain}>
                  <strong>{getSchool(item)}</strong>
                  <span style={styles.contactName}>{getName(item)}</span>
                  <span style={styles.meta}>{getRole(item)}</span>
                  <span style={styles.email}>{getEmail(item)}</span>
                  {item.schoolProfile?.modulesOfInterest?.length ? (
                    <span style={styles.modulesPreview}>
                      {item.schoolProfile.modulesOfInterest.slice(0, 3).join(" • ")}
                    </span>
                  ) : null}
                </div>
                <div className="pp-row-right" style={styles.rowRight}>
                  <span style={{ ...styles.pill, ...statusTone(item.status) }}>
                    {statusLabel(item.status)}
                  </span>
                  <span style={styles.meta}>{formatDate(requestDate(item))}</span>
                  <span style={styles.view}>Open profile →</span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div style={styles.empty}>No demo requests match this view.</div>
        )}
      </section>

      {selected ? (
        <div
          style={styles.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <aside className="pp-scroll pp-drawer" style={styles.drawer}>
            <div style={styles.drawerTop}>
              <div>
                <span style={{ ...styles.pill, ...statusTone(selected.status) }}>
                  {statusLabel(selected.status)}
                </span>
                <h2 style={styles.drawerTitle}>{getSchool(selected)}</h2>
                <div style={styles.meta}>Request ID: {selected.id}</div>
              </div>
              <button style={styles.close} onClick={() => setSelected(null)}>
                ×
              </button>
            </div>

            <div className="pp-two" style={styles.two}>
              <Detail label="Contact" value={getName(selected)} />
              <Detail label="Role" value={getRole(selected)} />
              <Detail label="Email" value={getEmail(selected)} />
              <Detail label="Phone" value={getPhone(selected)} />
            </div>

            <Panel title="Email response">
              <div style={styles.inline}>
                <select
                  className="pp-select"
                  style={styles.growInput}
                  value={emailTemplate}
                  onChange={(e) => setEmailTemplate(e.target.value)}
                >
                  {EMAIL_TEMPLATES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <button style={styles.primary} onClick={openEmail}>
                  Reply to Enquiry
                </button>
              </div>
            </Panel>

            <Panel title="Pipeline status">
              <select
                className="pp-select"
                style={styles.fullInput}
                value={normaliseDemoStatus(selected.status)}
                disabled={updatingStatus}
                onChange={(e) => changeStatus(e.target.value)}
              >
                {DEMO_REQUEST_STATUSES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Panel>

            <Panel title="Modules of interest">
              <div style={styles.moduleGrid}>
                {PRESENTPLUS_MODULES.map((module) => (
                  <label key={module} style={styles.checkRow}>
                    <input
                      type="checkbox"
                      checked={profileForm.modulesOfInterest.includes(module)}
                      onChange={() => toggleModule(module)}
                    />
                    <span>{module}</span>
                  </label>
                ))}
              </div>
            </Panel>

            <Panel title="Demo meeting">
              <div className="pp-two" style={styles.two}>
                <Field label="Date">
                  <input
                    type="date"
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.meeting.date}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        meeting: { ...old.meeting, date: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Time">
                  <input
                    type="time"
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.meeting.time}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        meeting: { ...old.meeting, time: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Google Meet / meeting link">
                  <input
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.meeting.meetingLink}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        meeting: { ...old.meeting, meetingLink: e.target.value },
                      }))
                    }
                    placeholder="https://meet.google.com/..."
                  />
                </Field>
                <Field label="Location">
                  <input
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.meeting.location}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        meeting: { ...old.meeting, location: e.target.value },
                      }))
                    }
                    placeholder="Online or school address"
                  />
                </Field>
              </div>
            </Panel>

            <Panel title="Contract and subscription">
              <div className="pp-two" style={styles.two}>
                <Field label="Contract status">
                  <select
                    className="pp-select"
                    style={styles.fullInput}
                    value={profileForm.contract.status}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        contract: { ...old.contract, status: e.target.value },
                      }))
                    }
                  >
                    {CONTRACT_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Plan">
                  <input
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.contract.plan}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        contract: { ...old.contract, plan: e.target.value },
                      }))
                    }
                    placeholder="Starter, Pro, Enterprise..."
                  />
                </Field>
                <Field label="Annual value">
                  <input
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.contract.annualValue}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        contract: {
                          ...old.contract,
                          annualValue: e.target.value,
                        },
                      }))
                    }
                    placeholder="£0.00"
                  />
                </Field>
                <Field label="Renewal date">
                  <input
                    type="date"
                    className="pp-input"
                    style={styles.fullInput}
                    value={profileForm.contract.renewalDate}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        contract: {
                          ...old.contract,
                          renewalDate: e.target.value,
                        },
                      }))
                    }
                  />
                </Field>
              </div>
            </Panel>

            <Panel title="Customer onboarding">
              <div className="pp-two" style={styles.two}>
                <Field label="Onboarding stage">
                  <select
                    className="pp-select"
                    style={styles.fullInput}
                    value={profileForm.onboarding.status}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        onboarding: {
                          ...old.onboarding,
                          status: e.target.value,
                        },
                      }))
                    }
                  >
                    {ONBOARDING_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={`Progress: ${profileForm.onboarding.progress}%`}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    style={{ width: "100%" }}
                    value={profileForm.onboarding.progress}
                    onChange={(e) =>
                      setProfileForm((old) => ({
                        ...old,
                        onboarding: {
                          ...old.onboarding,
                          progress: Number(e.target.value),
                        },
                      }))
                    }
                  />
                </Field>
              </div>
              <div style={styles.progressTrack}>
                <div
                  style={{
                    ...styles.progressBar,
                    width: `${profileForm.onboarding.progress}%`,
                  }}
                />
              </div>
            </Panel>

            <Panel title="Tasks and follow-ups">
              <textarea
                className="pp-textarea"
                style={styles.textarea}
                value={tasksText}
                onChange={(e) => setTasksText(e.target.value)}
                placeholder={"Send pricing\nCall on Friday\nFollow up after demo"}
              />
              {taskRows.length ? (
                <div style={styles.taskList}>
                  {taskRows.map((task, index) => {
                    const existing = (profileForm.tasks || []).find(
                      (item) => String(item.text || item).trim() === task
                    );
                    return (
                      <label key={`${task}-${index}`} style={styles.checkRow}>
                        <input
                          type="checkbox"
                          checked={Boolean(existing?.completed)}
                          onChange={() => toggleTask(index)}
                        />
                        <span
                          style={{
                            textDecoration: existing?.completed
                              ? "line-through"
                              : "none",
                          }}
                        >
                          {task}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </Panel>

            <button
              style={styles.saveProfile}
              disabled={savingProfile}
              onClick={saveSchoolProfile}
            >
              {savingProfile ? "Saving School Profile..." : "Save School Profile"}
            </button>

            <Panel title="Internal notes">
              <textarea
                className="pp-textarea"
                style={styles.textarea}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Record calls, objections, decisions or next steps..."
              />
              <button
                style={styles.darkButton}
                disabled={savingNotes || !noteDraft.trim()}
                onClick={saveNote}
              >
                {savingNotes ? "Saving..." : "Add Note"}
              </button>
              {selected.notes ? (
                <pre style={styles.notesHistory}>{selected.notes}</pre>
              ) : null}
            </Panel>

            <Panel title="Activity timeline">
              <div style={styles.timeline}>
                {timeline.map((entry, index) => (
                  <div key={index} style={styles.timelineItem}>
                    <span style={styles.dot} />
                    <div>
                      <strong>{entry.action || "Activity recorded"}</strong>
                      {entry.detail ? (
                        <div style={styles.timelineDetail}>{entry.detail}</div>
                      ) : null}
                      <div style={styles.meta}>
                        {formatDate(entry.date || entry.createdAt)}
                        {entry.actor ? ` • ${entry.actor}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                <div style={styles.timelineItem}>
                  <span style={{ ...styles.dot, background: "#16a34a" }} />
                  <div>
                    <strong>Request submitted</strong>
                    <div style={styles.meta}>
                      {formatDate(requestDate(selected))}
                    </div>
                  </div>
                </div>
              </div>
            </Panel>

            <div style={styles.dangerZone}>
              <div>
                <strong style={styles.dangerTitle}>
                  Permanently delete demo request
                </strong>
                <p style={styles.dangerText}>
                  Use this for test, duplicate or unwanted records. This cannot be
                  undone. Type DELETE to confirm.
                </p>
              </div>
              <div style={styles.inline}>
                <input
                  className="pp-input"
                  style={styles.growInput}
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder="Type DELETE"
                />
                <button
                  style={{
                    ...styles.deleteButton,
                    opacity: deleteText === "DELETE" && !deleting ? 1 : 0.5,
                  }}
                  disabled={deleteText !== "DELETE" || deleting}
                  onClick={handleDelete}
                >
                  {deleting ? "Deleting..." : "Delete Permanently"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section style={styles.panel}>
      <div style={styles.panelTitle}>{title}</div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function Detail({ label, value }) {
  return (
    <div style={styles.detail}>
      <span style={styles.fieldLabel}>{label}</span>
      <strong style={{ overflowWrap: "anywhere" }}>{value || "—"}</strong>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: 20,
    background: "#f5f7fb",
    color: "#0f172a",
    fontFamily:
      "Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "1fr 290px",
    gap: 22,
    padding: 28,
    borderRadius: 30,
    color: "#fff",
    background:
      "linear-gradient(135deg,#0f255f 0%,#2563eb 55%,#7c3aed 100%)",
    boxShadow: "0 26px 70px rgba(37,99,235,.22)",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 900,
    fontSize: 12,
    letterSpacing: ".12em",
    textTransform: "uppercase",
  },
  logo: { width: 28, height: 28, objectFit: "contain" },
  title: {
    margin: "14px 0 8px",
    fontSize: "clamp(38px,6vw,62px)",
    letterSpacing: "-.055em",
    lineHeight: 0.98,
  },
  subtitle: { maxWidth: 750, lineHeight: 1.65, opacity: 0.86 },
  backButton: {
    marginTop: 18,
    padding: "11px 15px",
    borderRadius: 13,
    border: "1px solid rgba(255,255,255,.3)",
    color: "#fff",
    background: "rgba(255,255,255,.12)",
    fontWeight: 850,
    cursor: "pointer",
  },
  heroCard: {
    borderRadius: 22,
    padding: 22,
    background: "rgba(255,255,255,.13)",
    border: "1px solid rgba(255,255,255,.25)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  heroLabel: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: ".1em",
    textTransform: "uppercase",
  },
  heroNumber: { fontSize: 54, letterSpacing: "-.05em" },
  error: {
    marginTop: 14,
    padding: 13,
    borderRadius: 14,
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 800,
  },
  success: {
    position: "sticky",
    top: 10,
    zIndex: 10001,
    marginTop: 14,
    padding: 13,
    borderRadius: 14,
    background: "#dcfce7",
    color: "#166534",
    fontWeight: 800,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(5,minmax(0,1fr))",
    gap: 12,
    marginTop: 16,
  },
  stat: {
    border: "1px solid #e5eaf2",
    background: "#fff",
    borderRadius: 18,
    padding: 15,
    textAlign: "left",
    cursor: "pointer",
  },
  statActive: {
    borderColor: "#2563eb",
    boxShadow: "0 0 0 4px rgba(37,99,235,.1)",
  },
  statNote: { display: "block", marginTop: 8, color: "#64748b", fontSize: 11, fontWeight: 750 },
  statNumber: { display: "block", fontSize: 31, marginTop: 10 },
  listCard: {
    marginTop: 16,
    border: "1px solid #e5eaf2",
    background: "#fff",
    borderRadius: 24,
    padding: 19,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 15,
    flexWrap: "wrap",
    alignItems: "end",
  },
  eyebrow: {
    color: "#2563eb",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: ".11em",
    textTransform: "uppercase",
  },
  sectionTitle: { margin: "4px 0 0", fontSize: 25 },
  filters: { display: "flex", gap: 9, flexWrap: "wrap" },
  search: {
    width: 330,
    maxWidth: "100%",
    padding: "12px 13px",
    border: "1px solid #dce3ee",
    borderRadius: 13,
  },
  select: {
    padding: "12px 13px",
    border: "1px solid #dce3ee",
    borderRadius: 13,
    background: "#fff",
  },
  list: {
    maxHeight: 650,
    overflow: "auto",
    display: "grid",
    gap: 9,
    marginTop: 16,
  },
  row: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "48px 1fr auto",
    gap: 13,
    alignItems: "center",
    padding: 14,
    border: "1px solid #edf1f7",
    borderRadius: 17,
    background: "#fff",
    textAlign: "left",
    cursor: "pointer",
    transition: "all .16s ease",
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: 15,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#dbeafe,#ede9fe)",
    color: "#3730a3",
    fontWeight: 950,
  },
  rowMain: { minWidth: 0, display: "grid", gap: 4 },
  contactName: { color: "#0f172a", fontSize: 12, fontWeight: 900 },
  rowRight: { display: "grid", justifyItems: "end", gap: 5 },
  meta: { color: "#64748b", fontSize: 11, fontWeight: 700 },
  email: { color: "#2563eb", fontSize: 12, fontWeight: 800 },
  modulesPreview: { color: "#7c3aed", fontSize: 11, fontWeight: 800 },
  view: { color: "#2563eb", fontSize: 12, fontWeight: 900 },
  pill: {
    display: "inline-flex",
    width: "fit-content",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
  },
  empty: { padding: 40, textAlign: "center", color: "#64748b" },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15,23,42,.45)",
    backdropFilter: "blur(6px)",
    display: "flex",
    justifyContent: "flex-end",
  },
  drawer: {
    width: "min(720px,100vw)",
    height: "100%",
    overflowY: "auto",
    padding: 22,
    background: "#f8fafc",
    boxShadow: "-25px 0 80px rgba(15,23,42,.22)",
  },
  drawerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  drawerTitle: { margin: "10px 0 4px", fontSize: 32 },
  close: {
    width: 40,
    height: 40,
    borderRadius: 12,
    border: "1px solid #dfe5ee",
    background: "#fff",
    fontSize: 24,
    cursor: "pointer",
  },
  two: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: 10,
    marginTop: 14,
  },
  detail: {
    border: "1px solid #e4eaf2",
    background: "#fff",
    padding: 13,
    borderRadius: 15,
  },
  panel: {
    marginTop: 12,
    border: "1px solid #e4eaf2",
    background: "#fff",
    padding: 15,
    borderRadius: 17,
  },
  panelTitle: {
    marginBottom: 11,
    color: "#64748b",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: ".09em",
    textTransform: "uppercase",
  },
  inline: { display: "flex", gap: 9, flexWrap: "wrap" },
  growInput: {
    flex: "1 1 220px",
    minWidth: 0,
    padding: "11px 12px",
    borderRadius: 12,
    border: "1px solid #dce3ee",
    background: "#fff",
  },
  fullInput: {
    width: "100%",
    minWidth: 0,
    padding: "11px 12px",
    borderRadius: 12,
    border: "1px solid #dce3ee",
    background: "#fff",
  },
  primary: {
    padding: "11px 14px",
    border: 0,
    borderRadius: 12,
    color: "#fff",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    fontWeight: 900,
    cursor: "pointer",
  },
  field: { display: "grid", gap: 6 },
  fieldLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: ".06em",
  },
  moduleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: 8,
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: 10,
    borderRadius: 12,
    background: "#f8fafc",
    fontSize: 13,
    fontWeight: 750,
  },
  progressTrack: {
    height: 10,
    background: "#e2e8f0",
    borderRadius: 999,
    overflow: "hidden",
    marginTop: 13,
  },
  progressBar: {
    height: "100%",
    background: "linear-gradient(90deg,#2563eb,#7c3aed)",
    borderRadius: 999,
  },
  textarea: {
    width: "100%",
    minHeight: 110,
    resize: "vertical",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #dce3ee",
    font: "inherit",
    lineHeight: 1.55,
  },
  taskList: { display: "grid", gap: 7, marginTop: 10 },
  saveProfile: {
    width: "100%",
    marginTop: 13,
    padding: "14px 16px",
    border: 0,
    borderRadius: 14,
    color: "#fff",
    background: "linear-gradient(135deg,#0f255f,#2563eb)",
    fontWeight: 950,
    cursor: "pointer",
  },
  darkButton: {
    marginTop: 9,
    padding: "11px 14px",
    border: 0,
    borderRadius: 12,
    color: "#fff",
    background: "#0f172a",
    fontWeight: 900,
    cursor: "pointer",
  },
  notesHistory: {
    whiteSpace: "pre-wrap",
    marginTop: 13,
    maxHeight: 260,
    overflowY: "auto",
    font: "inherit",
    fontSize: 12,
    lineHeight: 1.65,
    color: "#334155",
  },
  timeline: { display: "grid", gap: 15 },
  timelineItem: {
    display: "grid",
    gridTemplateColumns: "13px 1fr",
    gap: 10,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    marginTop: 5,
    background: "#2563eb",
  },
  timelineDetail: {
    marginTop: 4,
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.5,
  },
  dangerZone: {
    marginTop: 16,
    padding: 16,
    borderRadius: 17,
    border: "1px solid #fecaca",
    background: "#fff7f7",
  },
  dangerTitle: { color: "#991b1b" },
  dangerText: {
    margin: "6px 0 12px",
    color: "#7f1d1d",
    fontSize: 12,
    lineHeight: 1.55,
  },
  deleteButton: {
    border: 0,
    borderRadius: 12,
    padding: "11px 14px",
    color: "#fff",
    background: "#dc2626",
    fontWeight: 900,
    cursor: "pointer",
  },
};
